import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { Workbox } from 'workbox-window';
import './index.css';
import { store } from './store';
import router from './router';
import { setOfflineStatus as setOffline } from './store/slices/uiSlice';
import websocketService from './services/websocketService';
import App from './App';

/**
 * Application entry point
 * 
 * Initializes the React application with:
 * - Redux store for state management
 * - React Router for navigation
 * - Material-UI theme provider (handled in App.tsx for dynamic theme switching)
 * - Service worker registration for PWA capabilities
 * - WebSocket connection for real-time updates
 * - Offline detection and handling
 */

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  const wb = new Workbox('/sw.js');

  // Listen for service worker updates
  wb.addEventListener('waiting', () => {
    // Dispatch custom event that ServiceWorkerUpdate component will listen to
    window.dispatchEvent(new CustomEvent('swUpdateAvailable', { detail: wb }));
  });

  // Listen for service worker activation
  wb.addEventListener('activated', (event) => {
    // Reload page if this is not the first activation
    if (!event.isUpdate) {
      console.log('Service worker activated for the first time');
    } else {
      console.log('Service worker updated and activated');
    }
  });

  // Register the service worker
  wb.register()
    .then((registration) => {
      console.log('Service worker registered:', registration);
    })
    .catch((error) => {
      console.error('Service worker registration failed:', error);
    });
}

// Initialize offline detection
const updateOnlineStatus = () => {
  const isOffline = !navigator.onLine;
  store.dispatch(setOffline(isOffline));
  
  if (!isOffline) {
    // Reconnect WebSocket when coming back online
    websocketService.connect();
  }
};

// Set initial online/offline status
updateOnlineStatus();

// Listen for online/offline events
window.addEventListener('online', updateOnlineStatus);
window.addEventListener('offline', updateOnlineStatus);

// Initialize WebSocket connection if online
if (navigator.onLine) {
  websocketService.connect();
}

// Handle page visibility changes to manage WebSocket connection
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // Optionally disconnect WebSocket when page is hidden to save resources
    // websocketService.disconnect();
  } else {
    // Reconnect WebSocket when page becomes visible
    if (navigator.onLine) {
      websocketService.connect();
    }
  }
});

// Render the application
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
      <CssBaseline />
      <App>
        <RouterProvider router={router} />
      </App>
    </Provider>
  </StrictMode>
);